import React, { Fragment, useState, useEffect, useRef } from 'react';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import { getEndpointUrl } from '../../../utils/endPoints';
import Form from '../../2_Molecules/Form/Form';
import { languageController } from '../../../controllers/language/languageController';
import { useForm } from '../../../hooks/useForm';
import FormContainer from '../../2_Molecules/FormContainer/FormContainer';
import FormConfirmation from '../../2_Molecules/FormConfirmation/FormConfirmation';
import Checkbox from '../../1_Atoms/Forms/Checkbox/Checkbox';
import parse from 'html-react-parser';
import Modal from '../../2_Molecules/Modal/Modal';
import { pushToGTMDataLayer, closeModal, handleAnalyticsTrackingCodes } from '../../../utils/helpers';
import axios from 'axios';

interface FormExtraParams {
  language: string;
  formDisclaimer?: string;
}

interface SaveBuildModalProps {
  formExtraParams?: FormExtraParams;
}

interface SaveBuildFormData {
  [key: string]: string | number | boolean | Record<string, any>;
  firstname: string;
  lastname: string;
  email: string;
  buildname: string;
  build: Record<string, any>;
}

interface BuildData {
  modelCode?: string;
  optionCode?: string;
  tradeInValue?: number;
  downPaymentValue?: number;
  loyaltyChecked?: boolean;
  militaryChecked?: boolean;
  graduateChecked?: boolean;
  firstTimeOwnerChecked?: boolean;
  financeTerm?: number;
  leaseTerm?: number;
  accessories?: string[];
  SelectedWheelIndex?: string;
  SelectedColourExteriorIndex?: string;
  SelectedColourInteriorIndex?: string;
  selectedNonModelPackageIndex?: string;
  includeTaxes?: string;
  includeFees?: string;
  includeRebates?: string;
  annualMileage?: string;
  province?: string;
  selectedConnectedVehicleIndex?: number | null;
  selectedAddedProtectionIndex?: number | null;
  selectedAppearanceProtectionIndex?: number | null;
  selectedAppearanceProtectionPlanPackageIndex?: number | null;
  year?: string;
  carline?: string;
  trim?: string;
}

interface ApiResponse {
  status?: number;
  msg?: string;
}

interface RequestData {
  first_name: string;
  last_name: string;
  email: string;
  build_name: string;
  lang_code: string;
  build?: string;
}

const SaveBuildModal: React.FC<SaveBuildModalProps> = ({ formExtraParams = { language: 'en' } }) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { language, formDisclaimer } = formExtraParams;

  const initState: SaveBuildFormData = {
    firstname: '',
    lastname: '',
    email: '',
    buildname: '',
    build: {}
  };

  const validations = {
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    buildname: ['required'],
  };

  const getBuildData = (newData: RequestData): RequestData => {
    const selectedTrim = sessionStorage.getItem("selectedTrim");
    const selectedVehicle = sessionStorage.getItem("SelectedVehicle");
    const financialObj = sessionStorage.getItem("financialObj");
    const selectedAccessories = sessionStorage.getItem("selectedAccessories");
    const selectedWheelIndex = sessionStorage.getItem("SelectedWheelIndex");
    const selectedColourExteriorIndex = sessionStorage.getItem("SelectedColourExteriorIndex");
    const selectedColourInteriorIndex = sessionStorage.getItem("SelectedColourInteriorIndex");
    const selectedNonModelPackageIndex = sessionStorage.getItem("selectedNonModelPackageIndex");
    const includeTaxes = sessionStorage.getItem("includeTaxes");
    const includeFees = sessionStorage.getItem("includeFees");
    const includeRebates = sessionStorage.getItem("includeRebates");
    const annualMileage = sessionStorage.getItem("AnnualMileage");
    const province = sessionStorage.getItem("province");

    const selectedConnectedVehicleIndex = JSON.parse(sessionStorage.getItem("selectedConnectedVehicleIndex") || 'null');
    const selectedAddedProtectionIndex = JSON.parse(sessionStorage.getItem("selectedAddedProtectionIndex") || 'null');
    const selectedAppearanceProtectionIndex = JSON.parse(sessionStorage.getItem("selectedAppearanceProtectionIndex") || 'null');
    const selectedAppearanceProtectionPlanPackageIndex = JSON.parse(sessionStorage.getItem("selectedAppearanceProtectionPlanPackageIndex") || 'null');

    if (selectedTrim) {
      const selectedTrimParsed = JSON.parse(selectedTrim);
      newData.build_name = selectedTrimParsed.title ? selectedTrimParsed.title : '';
    }

    if (financialObj) {
      const financialObjParsed = JSON.parse(financialObj);
      const buildData: BuildData = {
        modelCode: financialObjParsed.based_on?.model_code,
        optionCode: financialObjParsed.based_on?.option_code,

        tradeInValue: financialObjParsed.tradeInValue,
        downPaymentValue: financialObjParsed.downPaymentValue,
        loyaltyChecked: financialObjParsed.loyaltyChecked,
        militaryChecked: financialObjParsed.militaryChecked,
        graduateChecked: financialObjParsed.graduateChecked,
        firstTimeOwnerChecked: financialObjParsed.firstTimeOwnerChecked,

        financeTerm: financialObjParsed.financeTerm,
        leaseTerm: financialObjParsed.leaseTerm,
      };

      // Add selected accessories (part number as an array)
      if (selectedAccessories) {
        const selectedAccessoriesParsed = JSON.parse(selectedAccessories);

        if (selectedAccessoriesParsed.length > 0) {
          buildData.accessories = selectedAccessoriesParsed.map((acc: any) => acc.part_number);
        }
      }

      if (selectedWheelIndex) buildData.SelectedWheelIndex = selectedWheelIndex;
      if (selectedColourExteriorIndex) buildData.SelectedColourExteriorIndex = selectedColourExteriorIndex;
      if (selectedColourInteriorIndex) buildData.SelectedColourInteriorIndex = selectedColourInteriorIndex;
      if (selectedNonModelPackageIndex) buildData.selectedNonModelPackageIndex = selectedNonModelPackageIndex;
      if (includeTaxes) buildData.includeTaxes = includeTaxes;
      if (includeFees) buildData.includeFees = includeFees;
      if (includeRebates) buildData.includeRebates = includeRebates;
      if (annualMileage) buildData.annualMileage = annualMileage;
      if (province) buildData.province = province;

      if (selectedConnectedVehicleIndex !== null) buildData.selectedConnectedVehicleIndex = selectedConnectedVehicleIndex;
      if (selectedAddedProtectionIndex !== null) buildData.selectedAddedProtectionIndex = selectedAddedProtectionIndex;
      if (selectedAppearanceProtectionIndex && selectedAppearanceProtectionPlanPackageIndex) {
        buildData.selectedAppearanceProtectionIndex = selectedAppearanceProtectionIndex;
        buildData.selectedAppearanceProtectionPlanPackageIndex = selectedAppearanceProtectionPlanPackageIndex;
      }

      // Add selected vehicle data.
      if (selectedVehicle) {
        const selectedVehicleParsed = JSON.parse(selectedVehicle);
        buildData.year = selectedVehicleParsed.year;
        buildData.carline = selectedVehicleParsed.carline;
        buildData.trim = selectedVehicleParsed.trim;
      }

      newData['build'] = JSON.stringify(buildData);
    }

    return newData;
  };

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState: initState as any,
    validations: validations as any,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        let reqData: RequestData = {
          first_name: String(data.firstname),
          last_name: String(data.lastname),
          email: String(data.email),
          build_name: String(data.buildname),
          lang_code: language
        };

        reqData = getBuildData(reqData);

        const params = new URLSearchParams(reqData as any);
        const endpoint = getEndpointUrl('save-build');
        if (!endpoint) return;
        const res = await axios.post(endpoint, params);
        const resData: ApiResponse = res.data;

        if (resData && resData.status === 200) {
          setIsSubmitted(true);
          pushToGTMDataLayer({ formevent: 'submit' });
          handleAnalyticsTrackingCodes(data, 'Form Submit', 'Save Build Modal', 'SaveBuildModal');
          setData(initState as any);
        } else {
          setFormError(resData.msg || 'An error occurred');
        }
      } catch (err) {
        console.log(err);
      }
    },
  });

  useEffect(() => {
  }, []);

  const { firstname, lastname, email, buildname } = data;

  return (
    <Modal
      className="mz-save-build-modal mz-modal-form"
      onClose={() => setIsSubmitted(false)}
      modalRef={modalRef}
    >
      <FormContainer
        className="mz-save-build"
        isSubmitted={isSubmitted}
        heading={(
          <Fragment>
            <h3>{languageController.getCopy('saveBuild', 'heading')}</h3>
            <p>{languageController.getCopy('saveBuild', 'subHeading')}</p>
          </Fragment>
        )}
        confirmation={(
          <FormConfirmation
            heading={languageController.getCopy('saveBuild', 'confirmationHeading') || ''}
            subHeading={languageController.getCopy('saveBuild', 'confirmationSubHeading') || ''}
            buttonLabel={languageController.getCopy('saveBuild', 'confirmationSubmit')}
            onClick={() => {
              closeModal(modalRef.current);
              setIsSubmitted(false);
            }}
          />
        )}
      >
        <Form
          onSubmit={handleSubmit}
          isDark={false}
          noValidate={true}
          errorMessage={formError || undefined}
        >
          <FormGroup md={2}>
            <Input
              type="text"
              name="firstname"
              label={languageController.getFormCopy('label', 'firstname')}
              value={String(firstname)}
              isRequired={true}
              errorMessage={errors.firstname}
              onChange={handleChange}
              maxLength={50}
            />
            <Input
              type="text"
              name="lastname"
              label={languageController.getFormCopy('label', 'lastname')}
              value={String(lastname)}
              isRequired={true}
              errorMessage={errors.lastname}
              onChange={handleChange}
              maxLength={50}
            />
          </FormGroup>
          <Input
            type="email"
            name="email"
            label={languageController.getFormCopy('label', 'email')}
            value={String(email)}
            isRequired={true}
            errorMessage={errors.email}
            onChange={handleChange}
            maxLength={320}
          />
          <Input
            type="text"
            name="buildname"
            label={languageController.getFormCopy('label', 'buildName')}
            value={String(buildname)}
            isRequired={true}
            errorMessage={errors.buildname}
            onChange={handleChange}
            maxLength={100}
          />
          <Button
            type="submit"
            className="mz-btn--primary mz-btn--red"
            label={languageController.getCopy('saveBuild', 'submit')}
          />
        </Form>
      </FormContainer>
    </Modal>
  );
};

export default SaveBuildModal;
